import React, { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  Outlet,
} from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
  from,
} from "@apollo/client";
import apolloLogger from "apollo-link-logger";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import {
  getImpersonationFromStorage,
  ImpersonationProvider,
} from "./contexts/ImpersonationContext";
import ImpersonationBanner from "./components/common/ImpersonationBanner";
import Layout from "./components/layout/Layout";
import LayoutAdmin from "./components/layout/LayoutAdmin";
import Start from "./pages/onboarding/Start";
import Dashboard from "./pages/admin/Dashboard";
import ByocDashboard from "./pages/admin/ByocDashboard";
import Cards from "./pages/admin/Cards";
import Rewards from "./pages/admin/Rewards";
import Transactions from "./pages/admin/Transactions";
import Statements from "./pages/admin/Statements";
import Integrations from "./pages/admin/Integrations";
import Allocations from "./pages/admin/Allocations";
import Locations from "./pages/admin/Locations";
import Users from "./pages/admin/Users";
import Contacts from "./pages/admin/Contacts";
import Analytics from "./pages/admin/Analytics";
import CreateEditInvoice from "./pages/admin/CreateEditInvoice";
import Support from "./pages/Support";
import Refer from "./pages/Refer";
import NotificationSettingsPage from "components/common/notifications/NotificationSettingsPage";
import Policies from "./pages/admin/Policies";
import Login from "./pages/authentication/Login";
import Logout from "./pages/authentication/Logout";
import Home from "./pages/Home";
import Maintenance from "./pages/Maintenance";
import Mobile from "./pages/Mobile";
import CardSettings from "./pages/admin/CardSettings";
import CardPrograms from "./pages/admin/CardPrograms";
import LinkedAccounts from "./pages/admin/LinkedAccounts";
import { FronteggProvider, useFeatureEntitlements } from "@frontegg/react";
import { useAuth } from "@frontegg/react";
import i18n from "./i8n";
import Redirect from "./pages/Redirect";
import ScrollToTop from "./components/common/ScrollToTop";
import ProtectedRoute from "./components/auth/protectedRoute";
import { myPathForAdminPath, ROUTE_GATES } from "./utils/apps";
import Inactive from "./pages/Inactive";
import Declined from "./pages/Declined";
import Pending from "./pages/Pending";
import DocumentUpload from "./pages/DocumentUpload";
import CardDetails from "./pages/mobile/CardDetails";
import NotificationSettings from "./pages/admin/NotificationSettings";
import * as Sentry from "@sentry/react";
import ActivatePhysicalCard from "./pages/ActivatePhysicalCard";
import { CircularProgress } from "@mui/material";
import InvoiceHistory from "./pages/admin/InvoiceHistory";
import PaymentLinks from "./pages/admin/PaymentLinks";
import Subscriptions from "./pages/admin/Subscriptions";
import { isPayHost } from "./utils/payHost";
import Role from "./enums/Role";
import PayInvoice from "pages/PayInvoice";
import PayLink from "pages/PayLink";
import ManageSubscription from "pages/ManageSubscription";
import Reimbursements from "pages/admin/Reimbursements";
import CompanySettings from "./pages/admin/CompanySettings";
import Branding from "./pages/admin/Branding";
import BankSettings from "./pages/admin/BankSettings";
import ApiAccess from "./pages/admin/ApiAccess";
import { DsRootTheme } from "./theme/DsRootTheme";
import { BrandingProvider, useBrandingContext } from "context/BrandingContext";
import { ColorSchemeProvider } from "context/ColorSchemeContext";
import CustomFields from "pages/admin/CustomFields";
import OnboardCompany from "pages/onboarding/OnboardCompany";
import ContactLoginScreen from "pages/authentication/ContactLogin";
import ProtectedContactRoute from "components/auth/ProtectedContactRoute";
import { ContactAuthProvider } from "hooks/useContactAuthContext";
import Accounts from "pages/platform/Accounts";
import AccountHolder from "pages/platform/AccountHolder";
import PlatformDashboard from "pages/platform/Dashboard";
import PlatformRewards from "pages/platform/Rewards";
import useShowRewards from "hooks/useShowRewards";
import usePlatformRewardsAvailable from "hooks/usePlatformRewardsAvailable";
import ContactDetails from "components/admin/contacts/external/ExternalContactDetails";
import ContactDetailsPage from "pages/admin/ContactDetails";
import Payments from "pages/admin/Payments";
import LayoutPlatform from "components/layout/LayoutPlatform";
import PlatformUsers from "pages/platform/PlatformUsers";
import PlatformApiAccess from "pages/platform/PlatformApiAccess";
import PlatformWebhooks from "pages/platform/PlatformWebhooks";
import PlatformEmbeddedComponents from "pages/platform/PlatformEmbeddedComponents";
import PlatformIntegrations from "pages/platform/PlatformIntegrations";
import PlatformIntegrationDetails from "pages/platform/PlatformIntegrationDetails";
import PlatformBranding from "pages/platform/PlatformBranding";
import EventLogs from "pages/platform/EventLogs";
import { useCheckUserRole, useIsMyOnlyRole } from "hooks/useCheckUserRole";
import { useAppEntitlements } from "hooks/useAppEntitlements";
import { UserMentionProvider } from "context/UserMentionProvider";
import ReceiptLibrary from "pages/admin/ReceiptLibrary";
import LayoutEmployee from "components/layout/LayoutEmployee";
import EmployeeDashboard from "pages/employee/Dashboard";
import EmployeeCards from "pages/employee/Cards";
import EmployeeTransactions from "pages/employee/Transactions";
import EmployeeReimbursements from "pages/employee/Reimbursements";
import EmployeeReceiptLibrary from "pages/employee/ReceiptLibrary";
import EmployeeActivateCard from "pages/employee/ActivateCard";
import useCanTakePayments from "hooks/useCanTakePayments";

const httpLink = createHttpLink({
  uri: `${process.env.REACT_APP_API_BASE_URL}/graphql`,
  credentials: "include",
});

/* Contact routes need Apollo without the employee session or its login redirect. */
const ContactApolloProvider = ({ children }: { children: React.ReactNode }) => {
  const client = useMemo(() => {
    const contactAuthLink = setContext((_, { headers }) => {
      const token = localStorage.getItem("CONTACT_JWT_TOKEN");

      return {
        headers: {
          ...headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      };
    });

    return new ApolloClient({
      link: from([contactAuthLink, httpLink]),
      cache: new InMemoryCache(),
    });
  }, []);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const authOptions = {
  keepSessionAlive: true,
  enableSessionPerTenant: true,
};

const contextOptions = {
  baseUrl: process.env.REACT_APP_FRONTEGG_URL as string,
  clientId: process.env.REACT_APP_FRONTEGG_CLIENT as string,
  appId: process.env.REACT_APP_FRONTEGG_APP_ID as string,
  beforeRequestInterceptor: (opts: any, url: string) => {
    localStorage.removeItem("FRONTEGG_OAUTH_ORGANIZATION");
    return opts;
  },
};

const TitleSetter = () => {
  const { platform } = useBrandingContext();
  const platformName = platform?.name || "Dashboard";

  // sets the tab title
  useEffect(() => {
    document.title = platformName;
  }, [platformName]);

  return null;
};

const ApolloClientProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const tokenRef = React.useRef<string | null>(null);

  // Uncomment this to redirect to login automatically
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate, isLoading]);

  useEffect(() => {
    tokenRef.current = user?.accessToken ?? null;
  }, [user?.accessToken]);

  useEffect(() => {
    if (!user) return;

    Sentry.setUser({
      email: user.email,
      fullName: user.name,
    });

    window.Atlas.call("identify", {
      userId: user.id,
      name: user.name,
      email: user.email,
      phoneNumber: user.phoneNumber,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const client = useMemo(() => {
    const authLink = setContext((_, { headers }) => {
      const token = tokenRef.current;
      return {
        headers: {
          ...headers,
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
      };
    });

    const impersonationLink = setContext((_, { headers }) => {
      const session = getImpersonationFromStorage();
      if (!session) return { headers };
      // Platform routes must always run as the actual admin (the impersonated
      // user typically lacks Platform role), so we skip the headers there.
      if (window.location.pathname.startsWith("/platform")) return { headers };
      return {
        headers: {
          ...headers,
          "x-impersonate-user-id": session.target.id,
          "x-impersonate-session-id": session.sessionId,
        },
      };
    });

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) =>
          console.log(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
          ),
        );
      }
      if (networkError) console.log(`[Network error]: ${networkError}`);
    });

    const linkConfig = [errorLink, authLink, impersonationLink, httpLink];
    if (process.env.REACT_APP_SENTRY_ENV === "development") {
      linkConfig.unshift(apolloLogger);
    }

    return new ApolloClient({
      link: from(linkConfig),
      cache: new InMemoryCache(),
    });
  }, []);

  if (!client) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="primary" />
      </div>
    );
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

const AuthenticationWrapper = () => {
  const isAuthenticated = useAuth().isAuthenticated;
  const isAuditorOrHigher = useCheckUserRole(Role.Auditor);
  const isManager = useCheckUserRole(Role.Manager, { exact: true });
  const isMyOnly = useIsMyOnlyRole();

  const renderAuthenticatedRoutes = () => {
    if (isAuditorOrHigher) {
      // Return all routes, even unprotected
      return <AdminRoutes />;
    } else if (isManager) {
      return <ManagerRoutes />;
    } else {
      return <EmployeeRoutes adminArea={!isMyOnly} />;
    }
  };
  if (!isAuthenticated) {
    // Return unprotected routes only
    return <UnauthenticatedRoutes />;
  } else {
    return (
      <ApolloClientProvider>
        <ImpersonationProvider>
          <ImpersonationBanner />
          <UserMentionProvider>
            {renderAuthenticatedRoutes()}
          </UserMentionProvider>
        </ImpersonationProvider>
      </ApolloClientProvider>
    );
  }
};

/**
 * Picks the dashboard for /admin/dashboard.
 *
 * A BYOC-only company has no Outpave wallet, no cash flow and no card issuing,
 * so it gets a dashboard built around linked-card spend instead of the legacy
 * one with half its panels hidden. Every other company is unaffected.
 */
const AdminDashboardRoute = () => {
  const { byocOnly } = useAppEntitlements();

  return byocOnly ? <ByocDashboard /> : <Dashboard />;
};

/** A /my-only role has no admin area, so an old /admin link opens the same page in /my. */
const AdminLinkToMy = () => {
  const { pathname, search } = useLocation();
  return <Navigate to={myPathForAdminPath(pathname, search)} replace />;
};

// A function rather than a component: `Routes` accepts only `Route` elements as children.
const myRoutes = (featureNotification: boolean) => (
  <Route
    path="/my"
    element={
      <ProtectedRoute gate={ROUTE_GATES.active}>
        <LayoutEmployee />
      </ProtectedRoute>
    }
  >
    <Route index element={<Navigate to="dashboard" replace />} />
    <Route path="dashboard" element={<EmployeeDashboard />} />
    <Route path="cards" element={<EmployeeCards />} />
    <Route path="transactions" element={<EmployeeTransactions />} />
    <Route path="reimbursements" element={<EmployeeReimbursements />} />
    <Route path="receipt-library" element={<EmployeeReceiptLibrary />} />
    {featureNotification && (
      <Route path="notifications" element={<NotificationSettingsPage />} />
    )}
    <Route path="activate" element={<EmployeeActivateCard />} />
  </Route>
);

export const AdminRoutes = () => {
  const showRewards = useShowRewards();
  const platformRewardsAvailable = usePlatformRewardsAvailable();
  const { byoc, byocOnly } = useAppEntitlements();
  const showCardPrograms = !byocOnly;
  const { isEntitled: featureInvoices } =
    useFeatureEntitlements("feature_invoices");
  const { isEntitled: featurePayments } =
    useFeatureEntitlements("feature_payments");
  const { isEntitled: featurePaymentLinks } = useFeatureEntitlements(
    "feature_payment_links",
  );
  const { isEntitled: featureSubscriptions } = useFeatureEntitlements(
    "feature_subscriptions",
  );
  const canTakePayments = useCanTakePayments();
  const { isEntitled: featureContacts } =
    useFeatureEntitlements("feature_contacts");
  const { isEntitled: featureStatements } =
    useFeatureEntitlements("feature_statements");
  const { isEntitled: featureNotification } = useFeatureEntitlements(
    "feature_notifications",
  );
  const { isEntitled: featureLocation } =
    useFeatureEntitlements("feature_locations");
  const { isEntitled: featureAllocations } = useFeatureEntitlements(
    "feature_allocations",
  );
  // The same pair `useSideBarItems` gates the branding nav entry on. Branding
  // was a tab inside a gated page before it was a route, so without these the
  // move to a route hands the URL to anyone.
  const { isEntitled: customColorFF } = useFeatureEntitlements(
    "feature_company_custom_brand",
  );
  const { isEntitled: featurePlatformLogoCompanySettings } =
    useFeatureEntitlements("feature_platform_logo_company_settings");
  const { isEntitled: featureApiAccess } =
    useFeatureEntitlements("feature_api_access");
  // The same gate `usePlatformSidebarItems` shows the platform Branding entry on.
  const canManagePlatformBranding = useCheckUserRole(Role.PlatformOwner);

  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });
  const isAccountant = useCheckUserRole(Role.Accountant, { exact: true });

  const isAuditorOrAccountant = useMemo(() => {
    return isAccountant || isAuditor;
  }, [isAccountant, isAuditor]);

  return (
    <SentryRoutes>
      <Route>
        <Route
          path="/onboarding/start"
          element={
            <ProtectedRoute gate={ROUTE_GATES.onboarding}>
              <Layout showFooter />
            </ProtectedRoute>
          }
        >
          <Route index element={<Start />} />
        </Route>
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute gate={ROUTE_GATES.onboarding}>
              <Layout showFooter alignTop />
            </ProtectedRoute>
          }
        >
          <Route path="business" element={<OnboardCompany />} />
        </Route>
        {/* An active company verifying itself with a second issuer. */}
        <Route
          path="/onboarding/issuer"
          element={
            <ProtectedRoute gate={ROUTE_GATES.issuerOnboarding}>
              <Layout showFooter alignTop />
            </ProtectedRoute>
          }
        >
          <Route path=":connectionId" element={<OnboardCompany />} />
        </Route>
        <Route
          path="/admin"
          element={
            <ProtectedRoute gate={ROUTE_GATES.spend}>
              <LayoutAdmin />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<AdminDashboardRoute />} />
          <Route path="transactions" element={<Transactions />} />
          <Route
            path="statements"
            element={featureStatements && <Statements />}
          />
          <Route path="reimbursements" element={<Reimbursements />} />
          <Route
            path="expenses/reimbursements"
            element={<Navigate to="/admin/reimbursements" replace />}
          />
          <Route
            path="expenses/*"
            element={<Navigate to="/admin/transactions" replace />}
          />
          <Route path="receipt-library" element={<ReceiptLibrary />} />
          <Route path="cards" element={<Cards />} />
          <Route path="contacts" element={featureContacts && <Contacts />} />
          <Route
            path="contacts/:contactId"
            element={featureContacts && <ContactDetailsPage />}
          />
          <Route path="users" element={<Users />} />
          {/* The BYOC-only home for Linked Accounts, which otherwise sits under
              Settings. Same page: for these companies linked cards are the spine
              of the product rather than a setting, so they get a top-level route
              and the Settings sub-item stands down. Gated to the menu's audience,
              so Auditors and Accountants cannot reach it by URL either. The
              sidebar derives its active item from the first path segment, which
              is also why this cannot just be a nav entry pointing at
              settings/linked-accounts. */}
          {byocOnly && !isAuditorOrAccountant && (
            <Route path="accounts" element={<LinkedAccounts />} />
          )}
          <Route path="analytics" element={<Analytics />} />
          <Route
            path="payment-links"
            element={featurePaymentLinks && canTakePayments && <PaymentLinks />}
          />
          <Route
            path="subscriptions"
            element={
              featureSubscriptions && canTakePayments && <Subscriptions />
            }
          />
          <Route
            path="invoices"
            element={featureInvoices && <InvoiceHistory />}
          />
          <Route
            path="invoices/send-an-invoice"
            element={featureInvoices && <CreateEditInvoice />}
          />
          <Route path="payments" element={featurePayments && <Payments />} />
          <Route path="settings" element={<div />} />
          <Route path="notifications" element={<div />} />
          <Route path="help" element={<div />} />
          {isAuditorOrAccountant ? (
            <Route path="settings">
              {featureAllocations && (
                <Route path="allocations" element={<Allocations />} />
              )}
              {featureLocation && (
                <Route path="locations" element={<Locations />} />
              )}
            </Route>
          ) : (
            <Route path="settings">
              {featureAllocations && (
                <Route path="allocations" element={<Allocations />} />
              )}
              {featureLocation && (
                <Route path="locations" element={<Locations />} />
              )}
              <Route path="card-settings" element={<CardSettings />} />
              {showCardPrograms && (
                <Route path="card-programs" element={<CardPrograms />} />
              )}
              <Route
                path="rewards"
                element={
                  <ProtectedRoute gate={ROUTE_GATES.owner}>
                    {showRewards && <Rewards />}
                  </ProtectedRoute>
                }
              />
              {byoc && (
                <Route path="linked-accounts" element={<LinkedAccounts />} />
              )}
              <Route path="integrations" element={<Integrations />} />
              <Route path="policies/transactions" element={<Policies />} />
              <Route path="policies/workflows" element={<Policies />} />
              <Route path="policies/password-policies" element={<Policies />} />
              <Route path="policies/attendee-policies" element={<Policies />} />
              <Route path="company-settings" element={<CompanySettings />} />
              {customColorFF && featurePlatformLogoCompanySettings && (
                <Route path="branding" element={<Branding />} />
              )}
              <Route path="bank-settings" element={<BankSettings />} />
              {featureApiAccess && (
                <Route path="api-access" element={<ApiAccess />} />
              )}
              <Route path="custom-fields" element={<CustomFields />} />
              <Route
                path="notifications-settings"
                element={<NotificationSettings />}
              />
            </Route>
          )}
          <Route path="support" element={<Support />} />
          <Route path="refer" element={<Refer />} />
          <Route
            path="user-notifications"
            element={<NotificationSettingsPage />}
          />
        </Route>
        <Route
          path="/platform"
          element={
            <ProtectedRoute gate={ROUTE_GATES.platform}>
              <LayoutPlatform />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<PlatformDashboard />} />
          <Route path="accounts" element={<Accounts />} />
          <Route
            path="rewards"
            element={platformRewardsAvailable ? <PlatformRewards /> : null}
          />
          <Route path="accounts/:accountId" element={<AccountHolder />} />
          <Route path="users" element={<PlatformUsers />} />
          <Route path="event-logs" element={<EventLogs />} />
          <Route path="refer" element={<Refer />} />
          {featureNotification && (
            <Route
              path="notifications"
              element={<NotificationSettingsPage />}
            />
          )}
          <Route path="settings">
            {canManagePlatformBranding && (
              <Route path="branding" element={<PlatformBranding />} />
            )}
            {featureApiAccess && (
              <>
                <Route path="api-access" element={<PlatformApiAccess />} />
                <Route path="webhooks" element={<PlatformWebhooks />} />
                <Route
                  path="embedded-components"
                  element={<PlatformEmbeddedComponents />}
                />
              </>
            )}
            {canManagePlatformBranding && (
              <>
                <Route path="integrations" element={<PlatformIntegrations />} />
                <Route
                  path="integrations/:integrationId"
                  element={<PlatformIntegrationDetails />}
                />
              </>
            )}
            {/* The one page these used to share, kept for old links. */}
            <Route
              path="platform"
              element={
                <Navigate
                  to={
                    canManagePlatformBranding
                      ? "/platform/settings/branding"
                      : featureApiAccess
                        ? "/platform/settings/api-access"
                        : "/platform/dashboard"
                  }
                  replace
                />
              }
            />
          </Route>
        </Route>
        <Route path="/logout" element={<Logout />} />
        <Route path="/login" element={<Login />} />
        <Route path="/redirect" element={<Redirect />} />
        <Route path="/card/:token" element={<CardDetails />} />
        <Route path="/inactive" element={<Layout />}>
          <Route index element={<Inactive />} />
        </Route>
        <Route path="/declined" element={<Layout />}>
          <Route index element={<Declined />} />
        </Route>
        <Route path="/maintenance" element={<Layout />}>
          <Route index element={<Maintenance />} />
        </Route>
        <Route path="/mobile" element={<Layout />}>
          <Route index element={<Mobile />} />
        </Route>
        <Route path="/pending" element={<Layout />}>
          <Route index element={<Pending />} />
        </Route>
        <Route path="/upload" element={<Layout />}>
          <Route index element={<DocumentUpload />} />
        </Route>
        <Route path="/activate" element={<ActivatePhysicalCard />} />
      </Route>
      <Route path="/logout" element={<Logout />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/contact/login"
        element={
          <ContactApolloProvider>
            <ContactAuthProvider>
              <ContactLoginScreen />
            </ContactAuthProvider>
          </ContactApolloProvider>
        }
      />
      <Route path="/redirect" element={<Redirect />} />
      <Route path="/card/:token" element={<CardDetails />} />
      <Route path="/inactive" element={<Layout />}>
        <Route index element={<Inactive />} />
      </Route>
      <Route path="/declined" element={<Layout />}>
        <Route index element={<Declined />} />
      </Route>
      <Route path="/maintenance" element={<Layout />}>
        <Route index element={<Maintenance />} />
      </Route>
      <Route path="/mobile" element={<Layout />}>
        <Route index element={<Mobile />} />
      </Route>
      <Route path="/pending" element={<Layout />}>
        <Route index element={<Pending />} />
      </Route>
      <Route path="/upload" element={<Layout />}>
        <Route index element={<DocumentUpload />} />
      </Route>
      <Route path="/activate" element={<ActivatePhysicalCard />} />

      <Route
        path="contact/details"
        element={
          <ContactApolloProvider>
            <ContactAuthProvider>
              <ProtectedContactRoute>
                <ContactDetails />
              </ProtectedContactRoute>
            </ContactAuthProvider>
          </ContactApolloProvider>
        }
      />

      {/* Pay tokens permit guest checkout; saved methods and account changes require a contact session. */}
      <Route
        path="/pay-invoice"
        element={
          <ContactApolloProvider>
            <ContactAuthProvider redirectOnLogout={false}>
              <Outlet />
            </ContactAuthProvider>
          </ContactApolloProvider>
        }
      >
        <Route index element={<PayInvoice />} />
        <Route path="checkout" element={<PayInvoice />} />
        <Route path="complete" element={<PayInvoice />} />
      </Route>

      {/* The page determines whether this link requires payer authentication. */}
      <Route path="/pay/:slug" element={<PayLinkAuth />}>
        <Route index element={<PayLink />} />
        <Route path="complete" element={<PayLink />} />
      </Route>
      {isPayHost() && (
        <Route path="/:slug" element={<PayLinkAuth />}>
          <Route index element={<PayLink />} />
          <Route path="complete" element={<PayLink />} />
        </Route>
      )}
      {/* Manage tokens permit cancellation; changing the saved method requires identity verification. */}
      <Route
        path="/manage-subscription/:token"
        element={
          <ContactAuthProvider redirectOnLogout={false}>
            <ManageSubscription />
          </ContactAuthProvider>
        }
      />

      {myRoutes(featureNotification)}

      <Route path="/" element={<Home />} />
      <Route path="*" element={<Home />} />
    </SentryRoutes>
  );
};

const ManagerRoutes = () => {
  const { isEntitled: featureNotification } = useFeatureEntitlements(
    "feature_notifications",
  );

  return (
    <SentryRoutes>
      <Route
        path="/admin"
        element={
          <ProtectedRoute gate={ROUTE_GATES.active}>
            <LayoutAdmin />
          </ProtectedRoute>
        }
      >
        <Route path="transactions" element={<Transactions />} />
        <Route path="reimbursements" element={<Reimbursements />} />
        <Route
          path="expenses/reimbursements"
          element={<Navigate to="/admin/reimbursements" replace />}
        />
        <Route
          path="expenses/*"
          element={<Navigate to="/admin/transactions" replace />}
        />
        <Route path="receipt-library" element={<ReceiptLibrary />} />
        <Route path="cards" element={<Cards />} />
        <Route path="users" element={<Users />} />
        <Route path="help" element={<div />} />
        <Route path="support" element={<Support />} />
        <Route path="refer" element={<Refer />} />
        {featureNotification && (
          <Route
            path="user-notifications"
            element={<NotificationSettingsPage />}
          />
        )}
      </Route>
      <Route path="/logout" element={<Logout />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/contact/login"
        element={
          <ContactAuthProvider>
            <ContactLoginScreen />
          </ContactAuthProvider>
        }
      />
      <Route
        path="contact/details"
        element={
          <ContactAuthProvider>
            <ProtectedContactRoute>
              <ContactDetails />
            </ProtectedContactRoute>
          </ContactAuthProvider>
        }
      />

      <Route
        path="/pay-invoice"
        element={
          <ContactAuthProvider redirectOnLogout={false}>
            <Outlet />
          </ContactAuthProvider>
        }
      >
        <Route index element={<PayInvoice />} />
        <Route path="checkout" element={<PayInvoice />} />
        <Route path="complete" element={<PayInvoice />} />
      </Route>

      <Route path="/pay/:slug" element={<PayLinkAuth />}>
        <Route index element={<PayLink />} />
        <Route path="complete" element={<PayLink />} />
      </Route>
      {isPayHost() && (
        <Route path="/:slug" element={<PayLinkAuth />}>
          <Route index element={<PayLink />} />
          <Route path="complete" element={<PayLink />} />
        </Route>
      )}
      <Route
        path="/manage-subscription/:token"
        element={
          <ContactAuthProvider redirectOnLogout={false}>
            <ManageSubscription />
          </ContactAuthProvider>
        }
      />

      {myRoutes(featureNotification)}
      <Route path="/redirect" element={<Redirect />} />
      <Route path="/card/:token" element={<CardDetails />} />
      <Route path="/inactive" element={<Layout />}>
        <Route index element={<Inactive />} />
      </Route>
      <Route path="/declined" element={<Layout />}>
        <Route index element={<Declined />} />
      </Route>
      <Route path="/maintenance" element={<Layout />}>
        <Route index element={<Maintenance />} />
      </Route>
      <Route path="/mobile" element={<Layout />}>
        <Route index element={<Mobile />} />
      </Route>
      <Route path="/pending" element={<Layout />}>
        <Route index element={<Pending />} />
      </Route>
      <Route path="/upload" element={<Layout />}>
        <Route index element={<DocumentUpload />} />
      </Route>
      <Route path="/activate" element={<ActivatePhysicalCard />} />
      <Route path="/" element={<Home />} />
      <Route path="*" element={<Home />} />
    </SentryRoutes>
  );
};

/** Below Manager. Approver and Integration keep these /admin pages; the /my-only roles do not. */
const EmployeeRoutes = ({ adminArea }: { adminArea: boolean }) => {
  const { isEntitled: featureNotification } = useFeatureEntitlements(
    "feature_notifications",
  );

  return (
    <SentryRoutes>
      {adminArea ? (
        <Route
          path="/admin"
          element={
            <ProtectedRoute gate={ROUTE_GATES.active}>
              <LayoutAdmin />
            </ProtectedRoute>
          }
        >
          <Route path="transactions" element={<Transactions />} />
          <Route path="reimbursements" element={<Reimbursements />} />
          <Route
            path="expenses/reimbursements"
            element={<Navigate to="/admin/reimbursements" replace />}
          />
          <Route
            path="expenses/*"
            element={<Navigate to="/admin/transactions" replace />}
          />
          <Route path="receipt-library" element={<ReceiptLibrary />} />
          <Route path="cards" element={<Cards />} />
          <Route path="help" element={<div />} />
          <Route path="support" element={<Support />} />
          <Route path="refer" element={<Refer />} />
          {featureNotification && (
            <Route
              path="user-notifications"
              element={<NotificationSettingsPage />}
            />
          )}
        </Route>
      ) : (
        <Route path="/admin/*" element={<AdminLinkToMy />} />
      )}
      <Route path="/logout" element={<Logout />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/contact/login"
        element={
          <ContactAuthProvider>
            <ContactLoginScreen />
          </ContactAuthProvider>
        }
      />
      <Route
        path="contact/details"
        element={
          <ContactAuthProvider>
            <ProtectedContactRoute>
              <ContactDetails />
            </ProtectedContactRoute>
          </ContactAuthProvider>
        }
      />

      <Route
        path="/pay-invoice"
        element={
          <ContactAuthProvider redirectOnLogout={false}>
            <Outlet />
          </ContactAuthProvider>
        }
      >
        <Route index element={<PayInvoice />} />
        <Route path="checkout" element={<PayInvoice />} />
        <Route path="complete" element={<PayInvoice />} />
      </Route>

      <Route path="/pay/:slug" element={<PayLinkAuth />}>
        <Route index element={<PayLink />} />
        <Route path="complete" element={<PayLink />} />
      </Route>
      {isPayHost() && (
        <Route path="/:slug" element={<PayLinkAuth />}>
          <Route index element={<PayLink />} />
          <Route path="complete" element={<PayLink />} />
        </Route>
      )}
      <Route
        path="/manage-subscription/:token"
        element={
          <ContactAuthProvider redirectOnLogout={false}>
            <ManageSubscription />
          </ContactAuthProvider>
        }
      />

      {myRoutes(featureNotification)}
      <Route path="/redirect" element={<Redirect />} />
      <Route path="/card/:token" element={<CardDetails />} />
      <Route path="/inactive" element={<Layout />}>
        <Route index element={<Inactive />} />
      </Route>
      <Route path="/declined" element={<Layout />}>
        <Route index element={<Declined />} />
      </Route>
      <Route path="/maintenance" element={<Layout />}>
        <Route index element={<Maintenance />} />
      </Route>
      <Route path="/mobile" element={<Layout />}>
        <Route index element={<Mobile />} />
      </Route>
      <Route path="/pending" element={<Layout />}>
        <Route index element={<Pending />} />
      </Route>
      <Route path="/upload" element={<Layout />}>
        <Route index element={<DocumentUpload />} />
      </Route>
      <Route path="/activate" element={<ActivatePhysicalCard />} />
      <Route path="/" element={<Home />} />
      <Route path="*" element={<Home />} />
    </SentryRoutes>
  );
};

/* Authentication depends on the fetched link, so this provider must allow anonymous access. */
const PayLinkAuth = () => (
  <ContactAuthProvider redirectOnLogout={false}>
    <Outlet />
  </ContactAuthProvider>
);

const UnauthenticatedRoutes = () => {
  return (
    <SentryRoutes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/contact/login"
        element={
          <ContactAuthProvider>
            <ContactLoginScreen />
          </ContactAuthProvider>
        }
      />
      <Route path="/inactive" element={<Layout />}>
        <Route index element={<Inactive />} />
      </Route>
      <Route path="/declined" element={<Layout />}>
        <Route index element={<Declined />} />
      </Route>
      <Route path="/maintenance" element={<Layout />}>
        <Route index element={<Maintenance />} />
      </Route>
      <Route path="/mobile" element={<Layout />}>
        <Route index element={<Mobile />} />
      </Route>
      <Route path="/card/:token" element={<CardDetails />} />
      <Route
        path="contact/details"
        element={
          <ContactAuthProvider>
            <ProtectedContactRoute>
              <ContactDetails />
            </ProtectedContactRoute>
          </ContactAuthProvider>
        }
      />
      <Route
        path="/pay-invoice"
        element={
          <ContactAuthProvider redirectOnLogout={false}>
            <Outlet />
          </ContactAuthProvider>
        }
      >
        <Route index element={<PayInvoice />} />
        <Route path="checkout" element={<PayInvoice />} />
        <Route path="complete" element={<PayInvoice />} />
      </Route>

      <Route path="/pay/:slug" element={<PayLinkAuth />}>
        <Route index element={<PayLink />} />
        <Route path="complete" element={<PayLink />} />
      </Route>
      {isPayHost() && (
        <Route path="/:slug" element={<PayLinkAuth />}>
          <Route index element={<PayLink />} />
          <Route path="complete" element={<PayLink />} />
        </Route>
      )}
      <Route
        path="/manage-subscription/:token"
        element={
          <ContactAuthProvider redirectOnLogout={false}>
            <ManageSubscription />
          </ContactAuthProvider>
        }
      />
      <Route path="/" element={<Home />} />
      <Route path="*" element={<Login />} />
    </SentryRoutes>
  );
};

const App = () => {
  const [loading, setLoading] = useState(false);

  return (
    <React.Fragment>
      <BrowserRouter>
        <I18nextProvider i18n={i18n}>
          <FronteggProvider
            contextOptions={contextOptions}
            authOptions={authOptions}
            entitlementsOptions={{ enabled: true }}
            customLoader={setLoading}
          >
            <BrandingProvider>
              {/* The app's only theme, the sole :root token writer, and the one toast host. */}
              <DsRootTheme>
                <ColorSchemeProvider>
                  {/* ✅ Remove setIsBrandingLoaded prop */}
                  <TitleSetter />
                  <LoadingOverlay loading={loading} />{" "}
                  {/* ✅ Move to separate component */}
                  <AuthenticationWrapper />
                </ColorSchemeProvider>
              </DsRootTheme>
            </BrandingProvider>
          </FronteggProvider>
        </I18nextProvider>
        <ScrollToTop />
      </BrowserRouter>
    </React.Fragment>
  );
};

const LoadingOverlay = ({ loading }: { loading: boolean }) => {
  const { isBrandingLoaded } = useBrandingContext();

  if (isBrandingLoaded && !loading) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgb(255, 255, 255)",
        zIndex: 9999,
      }}
    >
      <CircularProgress color="primary" />
    </div>
  );
};

export default App;
